import { ApplicationElement, html } from '@beunity/lit-components';

import '../file_icon/element.js';
import translations from './translations.js';
import style from './style.lit.css';

/**
 * Link to an uploaded file.
 *
 * @prop createdAt - Files upload date
 * @prop ownerInfo - Name of the file owner (optional)
 * @prop sizeInfo - Size of the file (optional)
 * @prop imgUrl - Url of the image preview (optional)
 * @prop tags - File tags (optional)
 * @prop maxTagsCount - Maximum number of rendered tags (optional)
 * @prop layout - The layout of the element (options: thumb, list)
 *
 * @slot default - File title
 */

export default class File extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    createdAt: { type: Date },
    sizeInfo: { type: String },
    ownerInfo: { type: String },
    imgUrl: { type: String },
    tags: { type: Array },
    maxTagsCount: { type: Number },
    layout: { type: String, reflect: true },
    ext: { type: String, state: true },
  };

  constructor() {
    super();
    this.layout = 'thumb';
    this.tags = [];
  }

  get maxTagsCount() {
    return this.layout == 'thumb' ? 6 : 3;
  }

  willUpdate(changedProperties) {
    // When updating bu-file attributes via rails and data-live feature, layout is changed to nil and files-wrapper component do not trigger slot change event so we are checking it manually
    if (changedProperties.has('layout') && !this.layout) {
      const wrapper = this.closest('bu-files-wrapper');

      if (wrapper) this.layout = wrapper.layout;
    }
  }

  onSlotChange(e) {
    const fileName = e.target
      .assignedElements({ flatten: true })[0]
      ?.innerText.trim();

    if (fileName) {
      this.ext = fileName.split('.').pop();
    }
  }

  renderTags() {
    return html`
      <div class="tags">
        ${this.tags.slice(0, this.maxTagsCount).map(tag => this.renderTag(tag))}
      </div>
    `;
  }

  renderTag(tag) {
    return html`
      <span class="tag color-${tag.color}" title=${tag.name}></span>
    `;
  }

  renderIcon() {
    return html` <bu-file-icon ext=${this.ext}></bu-file-icon> `;
  }

  renderThumbFigure() {
    return this.imgUrl ? html`<img src=${this.imgUrl} />` : this.renderIcon();
  }

  renderThumb() {
    return html`
      <div class="thumb-wrapper">
        <div class="figure">${this.renderThumbFigure()}</div>
        <div class="info">
          ${this.renderIcon()}
          <slot @slotchange=${this.onSlotChange}></slot>
        </div>
      </div>
    `;
  }

  renderList() {
    return html`
      ${this.renderIcon()}
      <div class="info">
        <slot @slotchange=${this.onSlotChange}></slot>
        <span class="owner">${this.ownerInfo}</span>
        <span class="created-at">
          <span>${this.t('uploaded')}</span>
          ${this.i18n.l('date.formats.medium', this.createdAt)}
        </span>
        <span class="size">${this.sizeInfo}</span>
      </div>
    `;
  }

  render() {
    return html`
      ${this.tags.length ? this.renderTags() : null}
      ${this.layout === 'list' ? this.renderList() : this.renderThumb()}
    `;
  }
}

File.registerTagName('bu-file');

customElements.get('bu-file') || customElements.define('bu-file', File);
